import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock, Share2 } from "lucide-react";
import { toast } from "sonner";
import logoMark from "@/assets/smg-logo-mark.png";
import { Eyebrow, Reveal } from "@/components/Shared";
import { GUIDES } from "@/content/guides";

const SITE = "https://smgsportsmgt.com";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ink text-slate-50">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-3 sm:px-12">
          <Link to="/" data-testid="guides-logo-link">
            <span className="relative inline-block">
              <img src={logoMark} alt="SMG Sports Management" className="h-10 w-auto" />
              <span className="absolute -right-3 -top-1 font-mono text-[9px] text-slate-400">™</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="guides-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>
      <main className="relative mx-auto w-full max-w-6xl px-6 py-14 sm:px-12">{children}</main>
      <footer className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-6 py-8 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-12">
          <p>A Division of SMG Media Group · Carlsbad, CA</p>
          <p>© 2026 SMG Media Group</p>
        </div>
      </footer>
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(path) {
  const href = `${SITE}${path}`;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  let og = document.querySelector('meta[property="og:url"]');
  if (!og) {
    og = document.createElement("meta");
    og.setAttribute("property", "og:url");
    document.head.appendChild(og);
  }
  og.setAttribute("content", href);
}

export function GuidesIndexPage() {
  useEffect(() => {
    document.title = "Pickleball Guides for Players & Brands | SMG Sports Management";
    setMeta(
      "description",
      "Guides from a pickleball athlete representation agency: what a pickleball agent does, how to get signed, and how top pickleball players build their brands."
    );
    setCanonical("/guides");
  }, []);

  return (
    <Shell>
      <Reveal>
        <Eyebrow className="mb-5">— Guides</Eyebrow>
        <h1 className="max-w-3xl font-display text-4xl uppercase leading-[1.02] sm:text-5xl lg:text-6xl">
          Learn the game <span className="text-volt">behind the game.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Straight answers on pickleball representation — what agents do, how to get
          signed, and how players turn results into brands. Written by the SMG team.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {GUIDES.map((g, i) => (
          <Reveal key={g.slug} delay={0.08 * i} className="h-full">
            <Link
              to={`/guides/${g.slug}`}
              data-testid={`guide-card-${g.slug}`}
              className="group flex h-full flex-col rounded-3xl border border-white/10 bg-surface p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-volt/50 hover:shadow-[0_20px_60px_rgba(132,204,22,0.1)]"
            >
              <span className="font-mono text-xs text-slate-600">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="mt-5 font-display text-2xl uppercase leading-snug text-white transition-colors duration-300 group-hover:text-volt">
                {g.title}
              </h2>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">
                {g.description}
              </p>
              <span className="mt-6 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                <span className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5" /> {g.minutes} min read
                </span>
                <ArrowUpRight className="h-5 w-5 text-slate-600 transition-all duration-300 group-hover:text-volt" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </Shell>
  );
}

export function GuideArticlePage() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);
  const others = GUIDES.filter((g) => g.slug !== slug);

  useEffect(() => {
    if (!guide) return;
    document.title = guide.seoTitle;
    setMeta("description", guide.description);
    setCanonical(`/guides/${guide.slug}`);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "guide-article-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: guide.title,
      description: guide.description,
      datePublished: guide.date,
      author: { "@type": "Organization", name: "SMG Sports Management Group International", url: SITE },
      publisher: { "@type": "Organization", name: "SMG Sports Management Group International", url: SITE },
      image: `${SITE}/og-image.jpg`,
      mainEntityOfPage: `${SITE}/guides/${guide.slug}`,
    });
    document.head.appendChild(script);
    return () => document.getElementById("guide-article-jsonld")?.remove();
  }, [guide]);

  if (!guide) {
    return (
      <Shell>
        <div className="py-24 text-center" data-testid="guide-not-found">
          <h1 className="font-display text-4xl uppercase text-white">Guide not found</h1>
          <Link
            to="/guides"
            data-testid="guide-notfound-back-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-volt px-8 py-3.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
          >
            All guides
          </Link>
        </div>
      </Shell>
    );
  }

  const shareGuide = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: guide.title, text: guide.description, url });
        window.gtag?.("event", "share", { method: "native", content_type: "guide" });
        return;
      }
      await navigator.clipboard.writeText(url);
      window.gtag?.("event", "share", { method: "clipboard", content_type: "guide" });
      toast.success("Guide link copied — paste it anywhere.");
    } catch (err) {
      if (err && err.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Guide link copied — paste it anywhere.");
      } catch {
        toast.error("Could not share — copy the link from your browser's address bar.");
      }
    }
  };

  return (
    <Shell>
      <article data-testid="guide-article">
        <Reveal>
          <Link
            to="/guides"
            data-testid="guide-article-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-volt"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All guides
          </Link>
          <h1 className="mt-6 max-w-3xl font-display text-4xl uppercase leading-[1.05] text-white sm:text-5xl">
            {guide.title}
          </h1>
          <p className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            <span>{guide.date}</span>
            <span className="h-1 w-1 rounded-full bg-volt" />
            <span className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> {guide.minutes} min read
            </span>
            <button
              onClick={shareGuide}
              data-testid="guide-share-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-slate-300 transition-colors hover:border-volt hover:text-volt"
              aria-label="Share this guide"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </p>
        </Reveal>

        <div className="mt-10 max-w-3xl space-y-6 border-t border-white/10 pt-10">
          {guide.body.map((block, i) => {
            if (block.t === "h2") {
              return (
                <Reveal key={i}>
                  <h2 className="pt-4 font-display text-2xl uppercase text-white sm:text-3xl">
                    {block.x}
                  </h2>
                </Reveal>
              );
            }
            if (block.t === "ul") {
              return (
                <ul key={i} className="space-y-3">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-sm leading-relaxed text-slate-300 sm:text-base">
                {block.x}
              </p>
            );
          })}
        </div>
      </article>

      <Reveal>
        <div
          className="mt-16 rounded-3xl border border-volt/30 bg-surface p-8 sm:p-12"
          data-testid="guide-cta-card"
        >
          <h2 className="font-display text-3xl uppercase leading-tight text-white sm:text-4xl">
            Ready for the <span className="text-volt">next step?</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Players: apply for representation in a few minutes. Brands: tell us what
            you're building and we'll match you with the right athletes.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/#apply"
              data-testid="guide-cta-athlete"
              className="inline-flex items-center gap-2 rounded-full bg-volt px-8 py-3.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
            >
              Apply for representation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/#apply"
              data-testid="guide-cta-brand"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 font-display text-sm uppercase tracking-wider text-white transition-colors hover:border-volt hover:text-volt"
            >
              Partner with an athlete
            </Link>
          </div>
        </div>
      </Reveal>

      <div className="mt-14">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">More guides</p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {others.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              data-testid={`guide-more-${g.slug}`}
              className="group rounded-3xl border border-white/10 bg-surface p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-volt/50"
            >
              <h3 className="font-display text-xl uppercase leading-snug text-white transition-colors duration-300 group-hover:text-volt">
                {g.title}
              </h3>
              <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors group-hover:text-volt">
                Read guide <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </Shell>
  );
}
